import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { primaryHover } from "shared/theme/trodoColors";
import { isBlankOrDefaultChartTitle } from "features/analytics/studio/utils/chartTitle";

/**
 * Centered dialog to name a chart before save (brand styling; replaces window.prompt).
 */
export default function ChartNameModal({
  open,
  onClose,
  initialValue = "",
  onConfirm,
  /** When true, primary button shows a working state */
  submitting = false,
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setValue(initialValue || "");
    setError("");
  }, [open, initialValue]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  if (!open || typeof document === "undefined") return null;

  const handleConfirm = () => {
    const t = value.trim();
    if (isBlankOrDefaultChartTitle(t)) {
      setError("Please enter a name for this chart.");
      return;
    }
    setError("");
    onConfirm?.(t);
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[220]"
        style={{ backgroundColor: "rgba(15, 23, 42, 0.12)" }}
        aria-hidden
        onMouseDown={() => {
          if (!submitting) onClose?.();
        }}
      />
      <div
        className="fixed left-1/2 top-1/2 z-[221] w-[min(400px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-xl border shadow-xl"
        style={{
          fontFamily: "Inter, sans-serif",
          backgroundColor: "var(--surface-base)",
          borderColor: theme.border || "var(--border-default)",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chart-name-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-2">
          <h2 id="chart-name-modal-title" className="text-base font-semibold" style={{ color: theme.textPrimary }}>
            Name this chart
          </h2>
          <p className="text-sm mt-1" style={{ color: theme.textMuted }}>
            Choose a name before saving.
          </p>
        </div>
        <div className="px-5 pb-4">
          <label htmlFor="chart-name-modal-input" className="sr-only">
            Chart name
          </label>
          <input
            id="chart-name-modal-input"
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !submitting) {
                e.preventDefault();
                handleConfirm();
              }
            }}
            disabled={submitting}
            autoFocus
            placeholder="Name"
            className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-colors"
            style={{
              borderColor: error ? "#f87171" : theme.border,
              color: theme.textPrimary,
              backgroundColor: theme.bgRaised || "var(--surface-base)",
            }}
          />
          {error ? (
            <p className="text-xs mt-2" style={{ color: "var(--feedback-error)" }}>
              {error}
            </p>
          ) : null}
        </div>
        <div
          className="flex justify-end gap-2 px-5 py-4 rounded-b-xl border-t"
          style={{ borderColor: theme.border, backgroundColor: theme.bgOverlay || "var(--surface-sunken)" }}
        >
          <button
            type="button"
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={{ color: theme.textSecondary }}
            onClick={() => !submitting && onClose?.()}
            onMouseEnter={(e) => {
              if (!submitting) e.currentTarget.style.backgroundColor = theme.bgOverlay;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium rounded-lg text-ink-onBrand transition-colors disabled:opacity-60"
            style={{ backgroundColor: theme.primaryLight }}
            onClick={handleConfirm}
            onMouseEnter={(e) => {
              if (!submitting) e.currentTarget.style.backgroundColor = primaryHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.primaryLight;
            }}
          >
            {submitting ? "Saving…" : "Continue"}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
